import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {isMonitoredLifecycle, isEdgeHealthy, isEdgeOffline, isEdgeUnhealthy} from '../constants/edgesConstants';

export const useEdgesFilter = (edges) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status');

  const [searchValue, setSearchValue] = useState(initialSearch);
  const [filterStatus, setFilterStatus] = useState(initialStatus ? [initialStatus] : []);
  const [filterLifecycle, setFilterLifecycle] = useState([]);
  const [filterTenant, setFilterTenant] = useState([]);
  const [sortOption, setSortOption] = useState('name_asc');

  useEffect(() => {
    setSearchValue(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setFilterStatus(initialStatus ? [initialStatus] : []);
  }, [initialStatus]);

  const availableTenants = useMemo(
    () =>
      [...new Set(edges.map((e) => e.immediateTenantName).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [edges]
  );

  const displayedEdges = useMemo(() => {
    let filtered = edges;

    if (filterStatus.length > 0) {
      filtered = filtered.filter((edge) => {
        const hasStatus = !!edge.currentStates;
        const isMonitored = isMonitoredLifecycle(edge.lifecycle?.status);
        if (filterStatus.includes('active') && isMonitored && isEdgeHealthy(edge)) return true;
        if (filterStatus.includes('issue') && isMonitored && isEdgeOffline(edge)) return true;
        if (filterStatus.includes('unhealthy') && isMonitored && isEdgeUnhealthy(edge)) return true;
        if (filterStatus.includes('unknown') && (!isMonitored || !hasStatus)) return true;
        return false;
      });
    }

    if (filterLifecycle.length > 0) {
      filtered = filtered.filter((edge) => filterLifecycle.includes(edge.lifecycle?.status));
    }

    if (filterTenant.length > 0) {
      filtered = filtered.filter((edge) => filterTenant.includes(edge.immediateTenantName));
    }

    if (searchValue) {
      const re = new RegExp(searchValue, 'i');
      filtered = filtered.filter((element) => {
        const addressStr = element.address ? JSON.stringify(element.address) : '';
        return (
          element.name?.match(re) ||
          re.exec(addressStr) ||
          element.lifecycle?.status?.match(re) ||
          element.id?.toString().match(re)
        );
      });
    }

    return [...filtered].sort((a, b) => {
      const cleanName = (str) => (str || '').replaceAll(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '').trim();
      const getStatusRank = (e) => {
        if (!e.currentStates?.deadmanCheck) return 3;
        if (e.currentStates.deadmanCheck.type === 401) return 2;
        return 1;
      };
      if (sortOption === 'name_asc') return cleanName(a.name).localeCompare(cleanName(b.name));
      if (sortOption === 'name_desc') return cleanName(b.name).localeCompare(cleanName(a.name));
      if (sortOption === 'update_asc')
        return (a.currentStates?.deadmanCheck?.timestamp || 0) - (b.currentStates?.deadmanCheck?.timestamp || 0);
      if (sortOption === 'update_desc')
        return (b.currentStates?.deadmanCheck?.timestamp || 0) - (a.currentStates?.deadmanCheck?.timestamp || 0);
      if (sortOption === 'lifecycle_asc')
        return (a.lifecycle?.status || '').localeCompare(b.lifecycle?.status || '');
      if (sortOption === 'lifecycle_desc')
        return (b.lifecycle?.status || '').localeCompare(a.lifecycle?.status || '');
      if (sortOption === 'status_asc') return getStatusRank(a) - getStatusRank(b);
      if (sortOption === 'status_desc') return getStatusRank(b) - getStatusRank(a);
      if (sortOption === 'address_asc') return (a.address || '').localeCompare(b.address || '');
      if (sortOption === 'address_desc') return (b.address || '').localeCompare(a.address || '');
      if (sortOption === 'ip_asc')
        return (a.status?.balena?.localIp?.toString() || '').localeCompare(b.status?.balena?.localIp?.toString() || '');
      if (sortOption === 'ip_desc')
        return (b.status?.balena?.localIp?.toString() || '').localeCompare(a.status?.balena?.localIp?.toString() || '');
      return 0;
    });
  }, [edges, searchValue, filterStatus, filterLifecycle, filterTenant, sortOption]);

  const toggleFilter = (filterState, setFilterState, value) => {
    setFilterState(
      filterState.includes(value)
        ? filterState.filter((item) => item !== value)
        : [...filterState, value]
    );
  };

  const isSelected = (filterState, value) => filterState.includes(value);

  return {
    searchValue,
    setSearchValue,
    filterStatus,
    setFilterStatus,
    filterLifecycle,
    setFilterLifecycle,
    filterTenant,
    setFilterTenant,
    sortOption,
    setSortOption,
    displayedEdges,
    availableTenants,
    toggleFilter,
    isSelected,
  };
};
